import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
    Grid,
    Button,
    Box,
    Typography,
    Stack,
} from '@mui/material';
import { Input, CommonModal, Select, MultiSelect } from '../../Common';
import Routes from '../../../routes';
import { trakrefBlue } from '../../../color_utils';
import Enums from '../../../enums';
import { DataArray } from '@mui/icons-material';
import { sanitizeAlphanumeric } from '../../../utils';

const AccountTypeOptions = [
    {
        value: Enums.AccountTypes.Tenant,
        name: "Tenant"
    },
    {
        value: Enums.AccountTypes.ContractorLite,
        name: "Contractor Lite",
    },
    {
        value: Enums.AccountTypes.Reclaim,
        name: "Reclaim"
    },
    {
        value: Enums.AccountTypes.SF6,
        name: "SF 6"
    },
    {
        value: Enums.AccountTypes.Contractor,
        name: "Trakref Pro"
    },
]

const requiredFields = ["name", "safetyClass", "containsAimRegulatedSubstance", "accountType"];

// US 16953: AR values are individually optional but at least one must be numeric.
const AR_FIELDS = ["ar4100Gwp", "ar5100Gwp", "ar620Gwp"];
const AR_LABELS = {
    ar4100Gwp: "AR4 100 GWP",
    ar5100Gwp: "AR5 100 GWP",
    ar620Gwp: "AR6 20 GWP"
};
const AR_AT_LEAST_ONE_MESSAGE = "At least one AR value (AR4, AR5, or AR6) must have a numeric value.";

const isBlankValue = value => value === undefined || value === null || String(value).trim() === '';
const isNumericValue = value => !isBlankValue(value) && Number.isFinite(Number(value));
const toNullableNumber = value => isNumericValue(value) ? Number(value) : null;

const initialFormValue = {
    id: 0,
    name: null,
    tradeName: null,
    casName: null,
    refrigerantTypeId: null,
    class: null,
    safetyClass: null,
    ar4100Gwp: null,
    ar5100Gwp: null,
    ar620Gwp: null,
    containsAimRegulatedSubstance: null,
    density: 0,
    gallonDensity: 0,
    accountType: [
        Enums.AccountTypes.Tenant,
        Enums.AccountTypes.ContractorLite,
        Enums.AccountTypes.Reclaim,
        Enums.AccountTypes.SF6,
        Enums.AccountTypes.Contractor
    ],
    sortOrder: 999
}

const MaterialTypeModal = ({
    id,
    modalOpen,
    onClose,
    filterOptions,
    refreshRequired
}) => {
    const [form, setForm] = useState(initialFormValue);
    const [missingFields, setMissingFields] = useState([]);
    const [arErrors, setArErrors] = useState({});

    // A number input reports '' for unparseable content, so the text the user typed is only
    // visible through validity.badInput. Without this, "NA" would silently save as NULL.
    const [arBadInput, setArBadInput] = useState({});

    // US 16953 AC9: a record saved with no AR value stays editable, so the AC2 rule is not applied
    // to it. State rather than a ref so the asterisks re-render once the record has loaded.
    const [existingHadArValue, setExistingHadArValue] = useState(false);

    const isFieldValid = (field) => missingFields.find(x => x === field);

    // AC2 only bites on create, or on a record that already held a value (AC9 exempts the rest).
    const arGroupRequired = !AR_FIELDS.some(field => isNumericValue(form[field]))
        && (id <= 0 || existingHadArValue);

    const onChangeForm = (field, value) => {
        if (field === "name") {
            value = sanitizeAlphanumeric(value);
        }
        if (AR_FIELDS.includes(field)) {
            setArErrors({});
        }
        setForm(prev => ({
            ...prev,
            [field]: value
        }))
    }

    const onChangeArField = (field, event) => {
        setArBadInput(prev => ({ ...prev, [field]: Boolean(event.target.validity?.badInput) }));
        onChangeForm(field, event.target.value);
    }

    const validateFields = () => {
        const missingFields = requiredFields.filter(field => {
            const value = form[field];
            if (field === "accountType") {
                return !Array.isArray(value) || value.length === 0;
            }
            return isBlankValue(value);
        });

        const nextArErrors = {};

        // AC3: an AR value is either numeric or blank; text such as "NA" is rejected.
        AR_FIELDS.forEach(field => {
            if (arBadInput[field] || (!isBlankValue(form[field]) && !isNumericValue(form[field])))
                nextArErrors[field] = `${AR_LABELS[field]} must be a numeric value or left blank.`;
        });

        // AC2: at least one AR value must be numeric. All three go red; AR4 carries the message
        // unless it already has its own, so a per-field message is never overwritten.
        if (arGroupRequired) {
            AR_FIELDS.forEach(field => { nextArErrors[field] = nextArErrors[field] || true; });
            nextArErrors.ar4100Gwp = nextArErrors.ar4100Gwp === true ? AR_AT_LEAST_ONE_MESSAGE : nextArErrors.ar4100Gwp;
        }

        setMissingFields([...missingFields]);
        setArErrors(nextArErrors);

        const arMessage = Object.values(nextArErrors).find(value => typeof value === 'string');
        if (arMessage)
            toast.error(arMessage, { duration: 6000 });

        return missingFields.length === 0 && Object.keys(nextArErrors).length === 0;
    }

    const submitForm = async () => {
        const isValid = validateFields();

        if (!isValid)
            return;

        const postConfig = {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: form.id,
                name: form.name,
                tradeName: form.tradeName,
                casName: form.casName,
                refrigerantTypeId: form.refrigerantTypeId ? parseInt(form.refrigerantTypeId) : null,
                class: form.class,
                ar4100Gwp: toNullableNumber(form.ar4100Gwp),
                ar5100Gwp: toNullableNumber(form.ar5100Gwp),
                ar620Gwp: toNullableNumber(form.ar620Gwp),
                containsAimRegulatedSubstance: form.containsAimRegulatedSubstance != null ? parseInt(form.containsAimRegulatedSubstance) : null,
                materialTypeSafetyClassId: form.safetyClass ? parseInt(form.safetyClass) : null,
                gwp: form.gwp,
                density: form.density,
                gallonDensity: form.gallonDensity,
                accountTypeIds: form.accountType,
                sortOrder: form.sortOrder
            })
        };

        const upsertResponse = await fetch(Routes.MaterialTypes.Upsert(), postConfig);

        const isUpdate = id > 0;

        if (upsertResponse.status === 200) {
            setForm(initialFormValue);
            setArErrors({});
            setArBadInput({});
            onClose();
            refreshRequired();
            return toast.success(`The Material Type has been successfully ${isUpdate ? 'updated' : 'created' }.`, { duration: 6000 });
        }

        if (upsertResponse.status === 400) {
            // Rule failures come back as a RuleResultTree array; a model-binding failure comes back
            // as a ValidationProblemDetails object, which has no map().
            const errors = await upsertResponse.json();
            const messages = Array.isArray(errors)
                ? errors.map(e => e.exceptionMessage).filter(Boolean).join('\n')
                : '';
            return toast.error(messages || `There was an error ${isUpdate ? 'updating' : 'creating'} the Material Type.`, { duration: 6000 });
        }

        if (upsertResponse.status === 400) {
            const errors = await upsertResponse.json();
            const messages = errors.map(e => e.exceptionMessage).filter(Boolean).join('\n');
            return toast.error(messages || `There was an error ${isUpdate ? 'updating' : 'creating'} the Material Type.`, { duration: 6000 });
        }

        return toast.error(`There was an error ${isUpdate ? 'updating' : 'creating' } the Material Type. Please try again.`, { duration: 6000 });
    }

    useEffect(() => {

        setExistingHadArValue(false);
        setArErrors({});
        setArBadInput({});

        if (id <= 0)
            return;

        const fetchData = async () => {
            const response = await fetch(Routes.MaterialTypes.GetById(id));
            const data = await response.json();

            setExistingHadArValue(AR_FIELDS.some(field => data[field] !== null && data[field] !== undefined));

            const accountTypeIds = [
                (data.accountTypeId & 1) > 0 ? Enums.AccountTypes.Tenant : "",
                (data.accountTypeId & 2) > 0 ? Enums.AccountTypes.ContractorLite : "",
                (data.accountTypeId & 4) > 0 ? Enums.AccountTypes.SF6 : "",
                (data.accountTypeId & 8) > 0 ? Enums.AccountTypes.Contractor : "",
                (data.accountTypeId & 16) > 0 ? Enums.AccountTypes.Reclaim: ""
            ].filter(x => x != "");

            setForm({
                id: data.id,
                name: data.name,
                tradeName: data.tradeName,
                casName: data.casName,
                refrigerantTypeId: data.refrigerantTypeId != null ? String(data.refrigerantTypeId) : null,
                class: data.class,
                ar4100Gwp: data.ar4100Gwp ?? '',
                ar5100Gwp: data.ar5100Gwp ?? '',
                ar620Gwp: data.ar620Gwp ?? '',
                containsAimRegulatedSubstance: data.containsAimRegulatedSubstance != null ? String(data.containsAimRegulatedSubstance) : null,
                safetyClass: data.materialTypeSafetyClassId != null ? String(data.materialTypeSafetyClassId) : null,
                gwp: data.gwp,
                density: data.density,
                gallonDensity: data.gallonDensity,
                accountType: accountTypeIds,
                sortOrder: data.sortOrder
            });
        }

        fetchData();
    }, [id])

    return (
        <CommonModal
            open={modalOpen}
            close={() => {
                setForm(initialFormValue);
                setMissingFields([]);
                setArErrors({});
                setArBadInput({});
                onClose();
            }}
            header={`${id <= 0 ? 'Add' : 'Edit'} Material Type`}
            width={"500px"}
        >
            <Grid container pl={6} pr={6}>
                <Grid item xs={12} lg={12} mb={2} pt={2} align='center' sx={{
                    "&MuiGrid-root MuiGrid-item": {
                        padding: "0px"
                    }
                }}>
                    <Stack spacing={"15px"}>
                        <Input
                            fullWidth
                            required
                            variant="outlined"
                            label="Name"
                            helperText="R-455A"
                            value={form.name}
                            error={isFieldValid("name")}
                            onChange={e => onChangeForm("name", e.target.value)}
                        />
                        <Input
                            fullWidth
                            variant="outlined"
                            label="Trade Name"
                            helperText="Solistice"
                            value={form.tradeName}
                            onChange={e => onChangeForm("tradeName", e.target.value)}
                        />
                        <Input
                            fullWidth
                            variant="outlined"
                            label="CAS Name"
                            helperText="R-23/R-116 (46/54)"
                            value={form.casName}
                            onChange={e => onChangeForm("casName", e.target.value)}
                        />
                        <Select
                            label={"Refrigerant Type"}
                            variant={"outlined"}
                            options={filterOptions.filter(x => x.property === 'refrigerantTypeName').map(o => ({ value: o.type, name: o.name }))}
                            value={form.refrigerantTypeId}
                            onChange={(e) => onChangeForm("refrigerantTypeId", e.target.value)}
                        />
                        <Select
                            label={"Class"}
                            variant={"outlined"}
                            options={filterOptions.filter(x => x.property === 'class')}
                            value={form.class}
                            onChange={(e) => onChangeForm("class", e.target.value)}
                        />
                        <Select
                            required
                            variant={"outlined"}
                            label={"Safety Class"}
                            options={filterOptions.filter(x => x.property === 'materialTypeSafetyClassName').map(o => ({ value: o.type, name: o.name }))}
                            value={form.safetyClass}
                            error={isFieldValid("safetyClass")}
                            onChange={(e) => onChangeForm("safetyClass", e.target.value)}
                        />
                        <Input
                            fullWidth
                            required={arGroupRequired}
                            variant="outlined"
                            label="AR4 100 GWP"
                            type="number"
                            value={form.ar4100Gwp ?? ''}
                            error={Boolean(arErrors.ar4100Gwp)}
                            helperText={typeof arErrors.ar4100Gwp === 'string' ? arErrors.ar4100Gwp : ' '}
                            onChange={e => onChangeArField("ar4100Gwp", e)}
                        />
                        <Input
                            fullWidth
                            required={arGroupRequired}
                            variant="outlined"
                            label="AR5 100 GWP"
                            type="number"
                            value={form.ar5100Gwp ?? ''}
                            error={Boolean(arErrors.ar5100Gwp)}
                            helperText={typeof arErrors.ar5100Gwp === 'string' ? arErrors.ar5100Gwp : ' '}
                            onChange={e => onChangeArField("ar5100Gwp", e)}
                        />
                        <Input
                            fullWidth
                            required={arGroupRequired}
                            variant="outlined"
                            label="AR6 20 GWP"
                            type="number"
                            value={form.ar620Gwp ?? ''}
                            error={Boolean(arErrors.ar620Gwp)}
                            helperText={typeof arErrors.ar620Gwp === 'string' ? arErrors.ar620Gwp : ' '}
                            onChange={e => onChangeArField("ar620Gwp", e)}
                        />
                        <Select
                            required
                            label={"Contains AIM Act Regulated Substance"}
                            variant={"outlined"}
                            options={filterOptions.filter(x => x.property === 'containsAimRegulatedSubstanceName').map(o => ({ value: o.type, name: o.name }))}
                            value={form.containsAimRegulatedSubstance}
                            error={isFieldValid("containsAimRegulatedSubstance")}
                            onChange={(e) => onChangeForm("containsAimRegulatedSubstance", e.target.value)}
                        />
                        <Input
                            fullWidth
                            variant="outlined"
                            label="Density (CUFt/Lb)"
                            type="number"
                            value={form.density}
                            onChange={e => onChangeForm("density", e.target.value)}
                        />
                        <Input
                            fullWidth
                            variant="outlined"
                            label="Density (Lb/Gal)"
                            type="number"
                            value={form.gallonDensity}
                            onChange={e => onChangeForm("gallonDensity", e.target.value)}
                        />
                        <MultiSelect
                            required
                            variant="outlined"
                            label="Account type"
                            options={AccountTypeOptions}
                            value={form.accountType}
                            error={isFieldValid("accountType")}
                            onChange={e => {
                                const {
                                    target: { value },
                                } = e;
                                onChangeForm("accountType", typeof value === 'string' ? value.split(',') : value);
                            }}
                        />
                        <Input
                            fullWidth
                            variant="outlined"
                            label="Sort Order"
                            type="number"
                            value={form.sortOrder}
                            onChange={e => onChangeForm("sortOrder", e.target.value)}
                        />
                        <Button
                            fullWidth
                            size='large'
                            variant='contained'
                            onClick={submitForm}
                            style={{
                                backgroundColor: trakrefBlue,
                                marginTop: "25px",
                                marginBottom: "15px"
                            }}
                        >
                            <Typography sx={{ padding: '1% 0%', color: '#fff', fontSize: 17, fontWeight: 400 }}>
                                SAVE MATERIAL TYPE
                            </Typography>
                        </Button>
                    </Stack>
                </Grid>
            </Grid>
        </CommonModal>
    );
};

export default MaterialTypeModal;
